(function (root, factory) {
  var data = typeof module === 'object' && module.exports
    ? require('./siokiuu-generator-size-data.js')
    : root && root.SiokiuuGeneratorSizeData;
  var api = factory(data || {});
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.SiokiuuGeneratorSizeCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (Data) {
  'use strict';

  var MAX_QUANTITY = 20;
  var GENERATOR_DERATE_WATTS = 200;
  var PARALLEL_UNIT_COUNT = 2;
  var PARALLEL_EFFICIENCY = 0.90;
  var SCENARIO_MARGIN = { home: 0.20, rv: 0.25, jobsite: 0.25 };
  var REMOVED_APPLIANCE_IDS = [
    'water-pump', 'sump-pump', 'well-pump', 'generic-power-tools', 'generic-appliance'
  ];
  var catalogs = Data.catalogs || { home: [], rv: [], jobsite: [] };
  var categories = Data.categories || { home: [], rv: [], jobsite: [] };
  var catalogIds = {};

  Object.keys(catalogs).forEach(function (mode) {
    catalogs[mode].forEach(function (item) { catalogIds[String(item.id)] = true; });
  });

  function finiteNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function slugify(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function isRemovedAppliance(item) {
    var id = slugify(item && (item.id || item.name));
    var name = String(item && item.name || '').toLowerCase();
    return REMOVED_APPLIANCE_IDS.indexOf(id) !== -1 ||
      /\b(water|sump|well)\s+pump\b/.test(name) ||
      /\bgeneric\s+(power\s+tools?|appliance)\b/.test(name);
  }

  function normalizeLoadType(value) {
    if (value === 'resistive') return 'steady';
    if (value === 'inductive') return 'motor';
    return ['steady', 'motor', 'electronic'].indexOf(value) !== -1 ? value : 'electronic';
  }

  function normalizeAppliance(item) {
    item = item || {};
    var id = slugify(item.id || item.name) || 'custom-appliance';
    var runningRaw = item.runningWatts != null ? item.runningWatts : item.running;
    var startingRaw = item.startingWatts != null ? item.startingWatts : item.starting;
    var runningWatts = finiteNumber(runningRaw, NaN);
    var startingWatts = finiteNumber(startingRaw, NaN);
    var quantity = Math.min(MAX_QUANTITY, Math.max(0, Math.round(finiteNumber(item.quantity, 0))));
    var validWattage = Number.isFinite(runningWatts) && runningWatts > 0 &&
      Number.isFinite(startingWatts) && startingWatts >= runningWatts;

    return {
      id: id,
      scenario: String(item.scenario || ''),
      category: String(item.category || 'Custom'),
      name: String(item.name || 'Custom appliance'),
      runningWatts: Number.isFinite(runningWatts) ? Math.max(0, Math.round(runningWatts)) : 0,
      startingWatts: Number.isFinite(startingWatts) ? Math.max(0, Math.round(startingWatts)) : 0,
      originalRunningWatts: Math.max(0, Math.round(finiteNumber(item.originalRunningWatts, runningWatts || 0))),
      originalStartingWatts: Math.max(0, Math.round(finiteNumber(item.originalStartingWatts, startingWatts || 0))),
      quantity: quantity,
      loadType: normalizeLoadType(item.loadType),
      voltage: finiteNumber(item.voltage, 120) === 240 ? 240 : 120,
      popular: Boolean(item.popular),
      userControlled: item.userControlled !== false,
      autoRestart: Boolean(item.autoRestart),
      staggerGroup: item.staggerGroup ? String(item.staggerGroup) : null,
      requiresNameplate: Boolean(item.requiresNameplate),
      wattageConfirmed: Boolean(item.wattageConfirmed),
      autoRecommendationEligible: item.autoRecommendationEligible !== false,
      unsupportedReason: item.unsupportedReason ? String(item.unsupportedReason) : null,
      notes: item.notes ? String(item.notes) : null,
      simultaneousStart: Boolean(item.simultaneousStart),
      isCustom: Boolean(item.isCustom) || !catalogIds[id],
      validWattage: validWattage
    };
  }

  function clone(items) {
    return (items || []).map(normalizeAppliance);
  }

  function sanitizeAppliances(items) {
    return clone(items).filter(function (item) {
      if (isRemovedAppliance(item)) return false;
      return catalogIds[item.id] || item.validWattage;
    });
  }

  function getScenarioCatalog(mode) {
    return clone(catalogs[mode] || catalogs.home || []).map(function (item) {
      item.quantity = 0;
      item.wattageConfirmed = false;
      return item;
    });
  }

  function getScenarioDefaults() {
    return [];
  }

  function getScenarioCategories(mode) {
    return (categories[mode] || []).slice();
  }

  function findCatalogAppliance(mode, value) {
    var target = slugify(value);
    return getScenarioCatalog(mode).find(function (item) {
      return item.id === target || slugify(item.name) === target;
    }) || null;
  }

  function searchCatalog(mode, query, category) {
    var needle = String(query || '').trim().toLowerCase();
    var selectedCategory = String(category || 'Popular');
    return getScenarioCatalog(mode).filter(function (item) {
      var categoryMatch = selectedCategory === 'All' ||
        (selectedCategory === 'Popular' ? item.popular : item.category === selectedCategory);
      var searchMatch = !needle || [item.name, item.category, item.notes].join(' ').toLowerCase().indexOf(needle) !== -1;
      return categoryMatch && searchMatch;
    });
  }

  function loadLabel(item) {
    item = normalizeAppliance(item);
    if (!item.autoRecommendationEligible) return 'Not Auto-Recommended';
    if (item.requiresNameplate) return 'Check Nameplate';
    if (item.loadType === 'motor') return 'Starting Surge';
    if (item.loadType === 'electronic') return 'Sensitive Electronics';
    return 'Steady Load';
  }

  function validateCustomAppliance(item) {
    var errors = [];
    var name = String(item && item.name || '').trim();
    var running = Number(item && item.runningWatts);
    var starting = Number(item && item.startingWatts);
    if (!name) errors.push('Enter a device name.');
    if (!Number.isFinite(running) || running <= 0) errors.push('Running watts must be greater than 0.');
    if (!Number.isFinite(starting) || starting <= 0) errors.push('Starting watts must be greater than 0.');
    if (Number.isFinite(running) && Number.isFinite(starting) && starting < running) {
      errors.push('Starting watts must be equal to or greater than running watts.');
    }
    return { valid: errors.length === 0, errors: errors };
  }

  function normalizeSelectedAppliance(item) {
    item = item || {};
    var normalized = normalizeAppliance(item);
    var runningRaw = item.runningWatts != null ? item.runningWatts : item.running;
    var startingRaw = item.startingWatts != null ? item.startingWatts : item.starting;
    var running = finiteNumber(runningRaw, 0);
    var starting = finiteNumber(startingRaw, running);
    normalized.runningWatts = Math.ceil(Math.max(0, running));
    normalized.startingWatts = Math.ceil(Math.max(normalized.runningWatts, starting));
    normalized.quantity = Math.min(MAX_QUANTITY, Math.max(1, Math.floor(finiteNumber(item.quantity, 1))));
    normalized.validWattage = Number.isFinite(Number(runningRaw)) && Number(runningRaw) >= 0;
    return normalized;
  }

  function selectedAppliances(items) {
    return (items || []).filter(function (item) {
      return finiteNumber(item && item.quantity, 0) > 0 && !isRemovedAppliance(item);
    }).map(normalizeSelectedAppliance);
  }

  function expandSelectedUnits(items) {
    var units = [];
    selectedAppliances(items).forEach(function (item) {
      for (var index = 0; index < item.quantity; index += 1) {
        units.push({
          id: item.id,
          name: item.name,
          runningWatts: item.runningWatts,
          startingWatts: item.startingWatts,
          startupDelta: Math.max(0, item.startingWatts - item.runningWatts)
        });
      }
    });
    return units;
  }

  function calculateStartupSequence(items) {
    var units = expandSelectedUnits(items).sort(function (a, b) {
      return b.startupDelta - a.startupDelta ||
        b.startingWatts - a.startingWatts ||
        a.name.localeCompare(b.name);
    });
    var alreadyRunning = 0;
    var startupSequencePeak = 0;
    units.forEach(function (unit) {
      startupSequencePeak = Math.max(startupSequencePeak, alreadyRunning + unit.startingWatts);
      alreadyRunning += unit.runningWatts;
    });
    return {
      startupSequencePeak: Math.ceil(startupSequencePeak),
      units: units
    };
  }

  function calculateDemand(items) {
    var selected = selectedAppliances(items);
    if (!selected.length) {
      return {
        status: 'EMPTY',
        runTotal: 0,
        peakNeeded: 0,
        maxStartupDelta: 0,
        startupSequencePeak: 0,
        selected: []
      };
    }
    var runTotal = selected.reduce(function (sum, item) {
      return sum + item.runningWatts * item.quantity;
    }, 0);
    var maxStartupDelta = selected.reduce(function (largest, item) {
      return Math.max(largest, Math.max(0, item.startingWatts - item.runningWatts));
    }, 0);
    var sequence = calculateStartupSequence(selected);
    return {
      status: 'READY',
      runTotal: Math.ceil(runTotal),
      peakNeeded: Math.ceil(runTotal + maxStartupDelta),
      maxStartupDelta: Math.ceil(maxStartupDelta),
      startupSequencePeak: sequence.startupSequencePeak,
      startupSequence: sequence.units,
      selected: selected
    };
  }

  function rowMetrics(item, forcedQuantity) {
    var normalized = normalizeAppliance(item);
    var quantity = forcedQuantity == null ? normalized.quantity : Math.max(0, forcedQuantity);
    var unitSurgeWatts = Math.max(0, normalized.startingWatts - normalized.runningWatts);
    var rowRunningWatts = normalized.runningWatts * quantity;
    var rowSurgeWatts = normalized.simultaneousStart ? unitSurgeWatts * quantity : (quantity > 0 ? unitSurgeWatts : 0);
    return {
      id: normalized.id,
      name: normalized.name,
      quantity: quantity,
      rowRunningWatts: rowRunningWatts,
      rowSurgeWatts: rowSurgeWatts,
      rowStartingTotal: rowRunningWatts + rowSurgeWatts
    };
  }

  function calculateAllAtOnce(items) {
    var rows = sanitizeAppliances(items).filter(function (item) { return item.quantity > 0; }).map(function (item) {
      return rowMetrics(item);
    });
    var runningWatts = rows.reduce(function (sum, row) { return sum + row.rowRunningWatts; }, 0);
    var largestSurgeWatts = rows.reduce(function (largest, row) { return Math.max(largest, row.rowSurgeWatts); }, 0);
    return {
      runningWatts: Math.round(runningWatts),
      startingWatts: Math.round(runningWatts + largestSurgeWatts),
      largestSurgeWatts: Math.round(largestSurgeWatts),
      rows: rows
    };
  }

  function combinations(groups, index, active, output) {
    if (index >= groups.length) {
      output.push(active.slice());
      return;
    }
    groups[index].forEach(function (item) {
      active.push(item);
      combinations(groups, index + 1, active, output);
      active.pop();
    });
  }

  function maximumLoadForStates(base, states) {
    var maximum = { runningWatts: 0, startingWatts: 0, largestSurgeWatts: 0 };
    (states.length ? states : [[]]).forEach(function (state) {
      var result = calculateAllAtOnce(base.concat(state));
      maximum.runningWatts = Math.max(maximum.runningWatts, result.runningWatts);
      maximum.startingWatts = Math.max(maximum.startingWatts, result.startingWatts);
      maximum.largestSurgeWatts = Math.max(maximum.largestSurgeWatts, result.largestSurgeWatts);
    });
    maximum.states = Math.max(1, states.length);
    return maximum;
  }

  function calculateStaggeredLoad(items) {
    var selected = sanitizeAppliances(items).filter(function (item) { return item.quantity > 0; });
    var assumesOneUnitAtATime = selected.some(function (item) {
      return item.staggerGroup && item.quantity > 1;
    });
    var base = selected.filter(function (item) { return !item.staggerGroup; });
    var grouped = {};
    selected.filter(function (item) { return item.staggerGroup; }).forEach(function (item) {
      if (!grouped[item.staggerGroup]) grouped[item.staggerGroup] = [];
      item.quantity = 1;
      grouped[item.staggerGroup].push(item);
    });
    var groupOptions = Object.keys(grouped).map(function (key) { return grouped[key]; });
    var states = [];
    if (groupOptions.length) combinations(groupOptions, 0, [], states);
    var maximum = maximumLoadForStates(base, states);
    maximum.assumesOneUnitAtATime = assumesOneUnitAtATime;
    return maximum;
  }

  function calculateLoads(items) {
    return { allAtOnce: calculateAllAtOnce(items), staggered: calculateStaggeredLoad(items) };
  }

  function isCompressor(item) {
    return /compressor/.test(item.id) || /air compressor/i.test(item.name);
  }

  function calculateJobsiteLoad(items, options) {
    options = options || {};
    var selected = sanitizeAppliances(items).filter(function (item) { return item.quantity > 0; });
    var allAtOnce = calculateAllAtOnce(selected);
    if (options.useTogether) {
      allAtOnce.mode = 'together';
      allAtOnce.autoRestartConflict = false;
      return allAtOnce;
    }

    var compressors = selected.filter(isCompressor);
    var majorTools = selected.filter(function (item) {
      return item.staggerGroup === 'jobsite-major-tools' || isCompressor(item);
    });
    var otherMotors = majorTools.filter(function (item) { return !isCompressor(item); });
    var unresolvedCompressor = compressors.some(function (item) { return item.autoRestart; }) &&
      otherMotors.length > 0 && !options.compressorOffConfirmed;
    var base = selected.filter(function (item) { return majorTools.indexOf(item) === -1; });
    var candidates = majorTools.slice();

    if (unresolvedCompressor) {
      base = base.concat(compressors);
      candidates = otherMotors;
    }

    var states = candidates.map(function (item) {
      item.quantity = 1;
      return [item];
    });
    var result = maximumLoadForStates(base, states);
    result.mode = 'one-at-a-time';
    result.autoRestartConflict = unresolvedCompressor;
    return result;
  }

  function hasStaggerConflict(items) {
    var counts = {};
    return sanitizeAppliances(items).filter(function (item) { return item.quantity > 0 && item.staggerGroup; }).some(function (item) {
      counts[item.staggerGroup] = (counts[item.staggerGroup] || 0) + 1;
      return item.quantity > 1 || counts[item.staggerGroup] > 1;
    });
  }

  function normalizeGenerator(generator, index) {
    generator = generator || {};
    var publishedRunning = finiteNumber(generator.publishedRunningWatts != null ? generator.publishedRunningWatts : generator.runningWatts, NaN);
    var publishedStarting = finiteNumber(generator.publishedStartingWatts != null ? generator.publishedStartingWatts : generator.startingWatts, NaN);
    if (!Number.isFinite(publishedRunning) || !Number.isFinite(publishedStarting)) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[SIOKIUU Find My Size] Excluding model with incomplete published wattage:', generator.modelId || generator.id || generator.name || index);
      }
      return null;
    }
    publishedRunning = Math.max(0, Math.floor(publishedRunning));
    publishedStarting = Math.max(publishedRunning, Math.floor(publishedStarting));
    return {
      id: String(generator.modelId || generator.id || ''),
      modelId: String(generator.modelId || generator.id || ''),
      name: String(generator.name || ''),
      publishedRunningWatts: publishedRunning,
      publishedStartingWatts: publishedStarting,
      isInverter: generator.isInverter !== false,
      isOpenFrame: Boolean(generator.isOpenFrame),
      hasTT30R: generator.hasTT30R === true,
      parallelHasTT30R: generator.parallelHasTT30R === true,
      parallelReady: generator.parallelReady === true,
      parallelKitUrl: String(generator.parallelKitUrl || ''),
      available: generator.available !== false,
      compatibleScenarios: Array.isArray(generator.compatibleScenarios) ? generator.compatibleScenarios.slice() : null,
      jobsiteCompatible: generator.jobsiteCompatible !== false,
      voltage: finiteNumber(generator.voltage, 120),
      order: Math.max(0, finiteNumber(generator.order, index)),
      source: generator
    };
  }

  function deriveSingleCapacity(generator, mode) {
    var margin = SCENARIO_MARGIN[mode] != null ? SCENARIO_MARGIN[mode] : SCENARIO_MARGIN.home;
    var planningRunning = Math.max(0, Math.floor(generator.publishedRunningWatts) - GENERATOR_DERATE_WATTS);
    var planningStarting = Math.max(planningRunning, Math.floor(generator.publishedStartingWatts) - GENERATOR_DERATE_WATTS);
    return {
      model: generator,
      unitCount: 1,
      planningRunning: planningRunning,
      planningStarting: planningStarting,
      safeRunning: Math.floor(planningRunning * (1 - margin))
    };
  }

  function deriveParallelCapacity(generator, mode) {
    var single = deriveSingleCapacity(generator, mode);
    var margin = SCENARIO_MARGIN[mode] != null ? SCENARIO_MARGIN[mode] : SCENARIO_MARGIN.home;
    var planningRunning = Math.floor(single.planningRunning * PARALLEL_UNIT_COUNT * PARALLEL_EFFICIENCY);
    var planningStarting = Math.floor(single.planningStarting * PARALLEL_UNIT_COUNT * PARALLEL_EFFICIENCY);
    return {
      model: generator,
      unitCount: PARALLEL_UNIT_COUNT,
      planningRunning: planningRunning,
      planningStarting: planningStarting,
      safeRunning: Math.floor(planningRunning * (1 - margin))
    };
  }

  function compatibleGenerator(generator, mode, options, pair) {
    if (!generator || !generator.available) return false;
    if (generator.voltage === 240) return false;
    if (generator.compatibleScenarios && generator.compatibleScenarios.indexOf(mode) === -1) return false;
    if (mode === 'jobsite' && !generator.jobsiteCompatible) return false;
    if (mode === 'rv' && options.needsTT30R === 'yes') {
      return pair ? generator.parallelHasTT30R === true : generator.hasTT30R === true;
    }
    return true;
  }

  function sortCapacity(a, b) {
    return a.planningRunning - b.planningRunning ||
      a.planningStarting - b.planningStarting ||
      a.model.order - b.model.order;
  }

  function headroomPercent(capacity, runTotal) {
    if (!capacity || capacity.planningRunning <= 0) return 0;
    return Math.max(0, Math.min(100, Math.floor((capacity.planningRunning - runTotal) / capacity.planningRunning * 100)));
  }

  function recommendationResult(status, title, message, demand, capacity, failureReason, label) {
    var model = capacity && capacity.model;
    return {
      status: status,
      title: title,
      message: message,
      demand: demand,
      capacity: capacity || null,
      failureReason: failureReason || null,
      generator: model ? model.source : null,
      model: model || null,
      unitCount: capacity ? capacity.unitCount : 0,
      headroomPercent: capacity ? headroomPercent(capacity, demand.runTotal) : 0,
      label: label || ''
    };
  }

  function demandWithExplicitMode(items, load) {
    var demand = calculateDemand(items);
    if (demand.status === 'EMPTY' || !load) return demand;
    var running = finiteNumber(load.runningWatts, NaN);
    var starting = finiteNumber(load.startingWatts, NaN);
    if (!Number.isFinite(running) || !Number.isFinite(starting)) return demand;
    demand.runTotal = Math.ceil(Math.max(0, running));
    demand.peakNeeded = Math.ceil(Math.max(demand.runTotal, starting));
    demand.maxStartupDelta = Math.max(0, demand.peakNeeded - demand.runTotal);
    return demand;
  }

  function evaluateRecommendation(generators, items, load, mode, options) {
    options = options || {};
    mode = SCENARIO_MARGIN[mode] != null ? mode : 'home';
    var demand = demandWithExplicitMode(items, load);
    var selected = demand.selected || [];
    if (demand.status === 'EMPTY') {
      return recommendationResult('EMPTY', 'Select appliances to get started', 'Select the appliances you want to power to see your recommended generator.', demand);
    }
    if (selected.some(function (item) { return item.voltage === 240; })) {
      return recommendationResult('A_NO_APPROVED_SOLUTION', 'No confirmed match', 'We don\'t have a confirmed compatible generator setup for the selected requirements. Contact us for a tailored recommendation.', demand, null, 'NO_ELIGIBLE_MODEL');
    }
    var ineligible = selected.find(function (item) { return !item.autoRecommendationEligible; });
    if (ineligible) {
      return recommendationResult('A_NO_APPROVED_SOLUTION', 'No confirmed match', ineligible.unsupportedReason || 'We don\'t have a confirmed compatible generator setup for the selected requirements. Contact us for a tailored recommendation.', demand, null, 'NO_ELIGIBLE_MODEL');
    }
    if (mode === 'jobsite') {
      var unconfirmed = selected.find(function (item) { return item.requiresNameplate && !item.wattageConfirmed; });
      if (unconfirmed) {
        return recommendationResult('A_NO_APPROVED_SOLUTION', 'Estimated only', 'We don\'t have a confirmed compatible generator setup for the selected requirements. Contact us for a tailored recommendation.', demand, null, 'NO_ELIGIBLE_MODEL');
      }
      if (load && load.autoRestartConflict) {
        return recommendationResult('A_NO_APPROVED_SOLUTION', 'No confirmed match', 'We don\'t have a confirmed compatible generator setup for the selected requirements. Contact us for a tailored recommendation.', demand, null, 'NO_ELIGIBLE_MODEL');
      }
    }

    var models = (generators || []).map(normalizeGenerator).filter(function (generator) {
      return compatibleGenerator(generator, mode, options, false);
    });
    var singles = models.map(function (generator) { return deriveSingleCapacity(generator, mode); }).sort(sortCapacity);
    if (!singles.length) {
      return recommendationResult('A_NO_APPROVED_SOLUTION', 'No confirmed match', 'We don\'t have a confirmed compatible generator setup for the selected requirements. Contact us for a tailored recommendation.', demand, null, 'NO_ELIGIBLE_MODEL');
    }

    var singleFit = singles.find(function (capacity) {
      return demand.runTotal <= capacity.safeRunning && demand.peakNeeded <= capacity.planningStarting;
    });
    if (singleFit) {
      var fitHeadroom = headroomPercent(singleFit, demand.runTotal);
      return recommendationResult(
        'C_SINGLE_FIT',
        'Recommended Generator',
        '\u2713 You\'re covered. The ' + singleFit.model.name + ' can run all selected items at the same time, with about ' + fitHeadroom + '% headroom to spare.',
        demand,
        singleFit
      );
    }

    var sequenceCandidates = singles.filter(function (capacity) {
      return demand.runTotal <= capacity.safeRunning &&
        demand.peakNeeded > capacity.planningStarting &&
        demand.startupSequencePeak <= capacity.planningStarting;
    });
    if (sequenceCandidates.length) {
      var sequenceFit = sequenceCandidates[sequenceCandidates.length - 1];
      return recommendationResult(
        'B_SINGLE_STARTUP_SEQUENCE',
        'Startup Sequence Recommended',
        'Almost there \u2014 use a startup sequence. The ' + sequenceFit.model.name + ' can handle the ' + demand.runTotal + 'W continuous load. Start the highest-surge motor-driven item first, let it settle, then start the remaining high-draw items one at a time.',
        demand,
        sequenceFit,
        null,
        'Starting surges are brief. With this startup order, your continuous load remains within the recommended planning range. This is normal load-management practice.'
      );
    }

    var pairModels = (generators || []).map(normalizeGenerator).filter(function (generator) {
      return generator && generator.parallelReady === true && compatibleGenerator(generator, mode, options, true);
    });
    var pairs = pairModels.map(function (generator) { return deriveParallelCapacity(generator, mode); }).sort(sortCapacity);
    var pairFit = pairs.find(function (capacity) {
      return demand.runTotal <= capacity.safeRunning && demand.peakNeeded <= capacity.planningStarting;
    });
    if (pairFit) {
      var pairReason = demand.runTotal > singles[singles.length - 1].safeRunning ? 'CONTINUOUS_LIMIT' : 'STARTING_LIMIT';
      var pairMessage = pairReason === 'CONTINUOUS_LIMIT'
        ? 'Your setup needs more continuous power than we recommend from one unit. To run all selected items together, use two identical ' + pairFit.model.name + ' units in parallel.'
        : 'Your setup\'s startup demand is above the conservative starting capacity of one unit. To support the selected load, use two identical ' + pairFit.model.name + ' units in parallel.';
      return recommendationResult(
        'A_PARALLEL_FIT',
        '2 \u00d7 ' + pairFit.model.name,
        pairMessage,
        demand,
        pairFit,
        pairReason,
        'Conservative parallel planning capacity: ' + pairFit.planningRunning + 'W running / ' + pairFit.planningStarting + 'W starting.'
      );
    }

    var maxSingle = singles.length ? singles[singles.length - 1] : null;
    var maxPair = pairs.length ? pairs[pairs.length - 1] : null;
    var failureReason = 'NO_ELIGIBLE_MODEL';
    if (maxPair) {
      failureReason = demand.runTotal > maxPair.safeRunning ? 'CONTINUOUS_LIMIT' : 'STARTING_LIMIT';
    } else if (maxSingle) {
      if (demand.runTotal > maxSingle.safeRunning) failureReason = 'CONTINUOUS_LIMIT';
      else if (demand.peakNeeded > maxSingle.planningStarting) failureReason = 'STARTING_LIMIT';
    }
    var noSolutionMessage = failureReason === 'CONTINUOUS_LIMIT'
      ? 'This is a heavy simultaneous load. It exceeds the recommended planning range of our current single-unit and approved same-model two-unit options. Use the items in separate sessions or contact us for a tailored recommendation.'
      : 'The startup demand is above the conservative planning capacity of our current single-unit and approved same-model two-unit options. Contact us for a tailored recommendation.';
    return recommendationResult('A_NO_APPROVED_SOLUTION', 'No approved setup found', noSolutionMessage, demand, null, failureReason);
  }

  function recommendGenerator(generators, requiredRunningWatts, requiredStartingWatts, mode, options) {
    var running = Math.max(0, finiteNumber(requiredRunningWatts, 0));
    var starting = Math.max(running, finiteNumber(requiredStartingWatts, running));
    var synthetic = [{ name: 'Selected load', runningWatts: running, startingWatts: starting, quantity: 1 }];
    var evaluated = evaluateRecommendation(generators, synthetic, null, mode, options || {});
    return evaluated.generator ? evaluated : null;
  }

  return {
    MAX_QUANTITY: MAX_QUANTITY,
    GENERATOR_DERATE_WATTS: GENERATOR_DERATE_WATTS,
    PARALLEL_UNIT_COUNT: PARALLEL_UNIT_COUNT,
    PARALLEL_EFFICIENCY: PARALLEL_EFFICIENCY,
    SCENARIO_MARGIN: Object.assign({}, SCENARIO_MARGIN),
    REMOVED_APPLIANCE_IDS: REMOVED_APPLIANCE_IDS.slice(),
    getScenarioCatalog: getScenarioCatalog,
    getScenarioDefaults: getScenarioDefaults,
    getScenarioCategories: getScenarioCategories,
    findCatalogAppliance: findCatalogAppliance,
    searchCatalog: searchCatalog,
    loadLabel: loadLabel,
    validateCustomAppliance: validateCustomAppliance,
    normalizeAppliance: normalizeAppliance,
    normalizeSelectedAppliance: normalizeSelectedAppliance,
    sanitizeAppliances: sanitizeAppliances,
    isRemovedAppliance: isRemovedAppliance,
    rowMetrics: rowMetrics,
    calculateDemand: calculateDemand,
    calculateStartupSequence: calculateStartupSequence,
    calculateAllAtOnce: calculateAllAtOnce,
    calculateStaggeredLoad: calculateStaggeredLoad,
    calculateJobsiteLoad: calculateJobsiteLoad,
    calculateLoads: calculateLoads,
    hasStaggerConflict: hasStaggerConflict,
    normalizeGenerator: normalizeGenerator,
    deriveSingleCapacity: deriveSingleCapacity,
    deriveParallelCapacity: deriveParallelCapacity,
    evaluateRecommendation: evaluateRecommendation,
    recommendGenerator: recommendGenerator
  };
});
